import React, { useState, useEffect, useRef } from 'react';
import { Container, TextField, Button, Typography, Grid, Paper, Accordion, AccordionSummary, AccordionDetails, Snackbar, Alert, Box, IconButton, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { fetchDepartments, saveDepartment, deleteDepartment } from '../api/DepartmentsApi'; // Import API functions

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [gridApi, setGridApi] = useState(null); // Define gridApi state
  const editSectionRef = useRef(null);

  useEffect(() => {
    const fetchDepartmentsData = async () => {
      try {
        const departmentsData = await fetchDepartments();
        setDepartments(departmentsData);
        if (departmentsData.length > 0) {
          setSelectedDepartment(departmentsData[0]);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
        setSnackbar({ open: true, message: 'Failed to fetch departments', severity: 'error' });
      }
    };

    fetchDepartmentsData();
  }, []);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', event.target.value);
    }
  };

  const handleRowClick = (event) => {
    const department = event.data;
    setSelectedDepartment(department);
    setTimeout(() => {
      editSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSelectedDepartment({ ...selectedDepartment, [name]: value });
  };

  const handleSave = async () => {
    try {
      await saveDepartment(selectedDepartment);
      setSnackbar({ open: true, message: 'Department saved successfully', severity: 'success' });
      const departmentsData = await fetchDepartments();
      setDepartments(departmentsData);
    } catch (error) {
      console.error('Error saving department:', error);
      setSnackbar({ open: true, message: 'Failed to save department', severity: 'error' });
    }
  };

  const handleNewDepartment = () => {
    setSelectedDepartment({
      departmentId: null,
      departmentName: '',
      description: '',
    });
  };

  const handleDeleteDepartment = async () => {
    try {
      await deleteDepartment(selectedDepartment.departmentId);
      setSnackbar({ open: true, message: 'Department deleted successfully', severity: 'success' });
      const departmentsData = await fetchDepartments();
      setDepartments(departmentsData);
      setSelectedDepartment(null);
    } catch (error) {
      console.error('Error deleting department:', error);
      setSnackbar({ open: true, message: 'Failed to delete department', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const columnDefs = [
    { headerName: 'Department Name', field: 'departmentName', sortable: true, filter: true },
    { headerName: 'Description', field: 'description', sortable: true, filter: true },
  ];

  const defaultColDef = {
    flex: 1,
    minWidth: 150,
    filter: true,
    sortable: true,
    resizable: true,
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
    if (params.api.getDisplayedRowCount() > 0) {
      const firstRow = params.api.getDisplayedRowAtIndex(0);
      params.api.selectNode(firstRow, true);
      setSelectedDepartment(firstRow.data);
      setTimeout(() => {
        editSectionRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          label="Search Departments"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          fullWidth
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
        />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ padding: 2 }}>
            <Box className="ag-theme-alpine" sx={{ height: 400, width: '100%' }}>
              <AgGridReact
                columnDefs={columnDefs}
                rowData={departments}
                pagination={true}
                paginationPageSize={10}
                onRowClicked={handleRowClick}
                onGridReady={onGridReady}
                defaultColDef={defaultColDef}
              />
            </Box>
          </Paper>
        </Grid>
        {selectedDepartment && (
          <Grid item xs={12}>
            <Accordion expanded ref={editSectionRef} sx={{ marginTop: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Edit Department</Typography>
                <Box ml={2}>
                  <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSave} sx={{ marginRight: 1 }}>
                    Save
                  </Button>
                  <Button variant="contained" color="secondary" startIcon={<DeleteIcon />} onClick={handleDeleteDepartment} sx={{ marginRight: 1 }}>
                    Delete
                  </Button>
                  <Button variant="contained" color="default" startIcon={<AddIcon />} onClick={handleNewDepartment}>
                    New
                  </Button>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Department Name"
                      variant="outlined"
                      fullWidth
                      name="departmentName"
                      value={selectedDepartment.departmentName}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Description"
                      variant="outlined"
                      fullWidth
                      name="description"
                      value={selectedDepartment.description || ''}
                      onChange={handleInputChange}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Departments;